export interface SpatialEntry {
  readonly xMeters: number;
  readonly zMeters: number;
}

export class SpatialHash<T extends SpatialEntry> {
  private readonly cells = new Map<string, T[]>();
  private readonly bucketPool: T[][] = [];

  constructor(readonly cellSizeMeters: number) {
    if (cellSizeMeters <= 0) {
      throw new Error("SpatialHash cell size must be positive");
    }
  }

  clear(): void {
    for (const bucket of this.cells.values()) {
      bucket.length = 0;
      this.bucketPool.push(bucket);
    }
    this.cells.clear();
  }

  insert(entry: T): void {
    const key = this.keyForPosition(entry.xMeters, entry.zMeters);
    const bucket = this.cells.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      const recycled = this.bucketPool.pop();
      if (recycled) {
        recycled.push(entry);
        this.cells.set(key, recycled);
      } else {
        this.cells.set(key, [entry]);
      }
    }
  }

  rebuild(entries: readonly T[]): void {
    this.clear();
    for (const entry of entries) {
      this.insert(entry);
    }
  }

  queryCircle(xMeters: number, zMeters: number, radiusMeters: number): T[] {
    const results: T[] = [];
    return this.queryCircleInto(results, xMeters, zMeters, radiusMeters);
  }

  queryCircleInto(
    results: T[],
    xMeters: number,
    zMeters: number,
    radiusMeters: number,
  ): T[] {
    results.length = 0;
    const minimumX = Math.floor((xMeters - radiusMeters) / this.cellSizeMeters);
    const maximumX = Math.floor((xMeters + radiusMeters) / this.cellSizeMeters);
    const minimumZ = Math.floor((zMeters - radiusMeters) / this.cellSizeMeters);
    const maximumZ = Math.floor((zMeters + radiusMeters) / this.cellSizeMeters);

    for (let cellZ = minimumZ; cellZ <= maximumZ; cellZ += 1) {
      for (let cellX = minimumX; cellX <= maximumX; cellX += 1) {
        const bucket = this.cells.get(`${cellX}:${cellZ}`);
        if (bucket) {
          results.push(...bucket);
        }
      }
    }
    return results;
  }

  get cellCount(): number {
    return this.cells.size;
  }

  private keyForPosition(xMeters: number, zMeters: number): string {
    return `${Math.floor(xMeters / this.cellSizeMeters)}:${Math.floor(zMeters / this.cellSizeMeters)}`;
  }
}
